import { Box, Button, ButtonGroup, HStack, Slider, Stat } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MIN_PRICE = 0;
const MAX_PRICE = 250;
const STEP = 5;

/* ---------------- URL helpers ---------------- */

const parsePriceFromUrl = search => {
  const params = new URLSearchParams(search);
  const raw = params.get('price');

  if (!raw) return [MIN_PRICE, MAX_PRICE];

  const [min, max] = raw.split(',').map(Number);

  if (Number.isNaN(min) || Number.isNaN(max)) {
    return [MIN_PRICE, MAX_PRICE];
  }

  return [Math.max(MIN_PRICE, min), Math.min(MAX_PRICE, max)];
};

const SearchFiltersPrice = ({ updateQueryParams }) => {
  const location = useLocation();
  const [isDirty, setIsDirty] = useState(false);
  // ---- URL source of truth
  const priceFromUrl = useMemo(() => parsePriceFromUrl(location.search), [location.search]);

  // ---- LOCAL STATE (instant UI)
  const [priceRange, setPriceRange] = useState(priceFromUrl);

  // ---- URL → STATE sync
  useEffect(() => {
    setPriceRange(priceFromUrl);
  }, [priceFromUrl]);

  useEffect(() => {
    const isDefault = priceRange[0] === MIN_PRICE && priceRange[1] === MAX_PRICE;

    setIsDirty(!isDefault);
  }, [priceRange]);

  /* ---------------- Handlers ---------------- */

  const applyPrice = () => {
    const isDefault = priceRange[0] === MIN_PRICE && priceRange[1] === MAX_PRICE;

    updateQueryParams({
      price: isDefault ? null : priceRange.join(','),
    });
  };

  const clearPrice = () => {
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    setIsDirty(false);
    updateQueryParams({ price: null });
  };

  /* ---------------- Render ---------------- */

  return (
    <Box>
      <HStack mb={3} style={{ justifyContent: 'space-between' }}>
        <Stat.Root>
          <Stat.Label>Min</Stat.Label>
          <Stat.ValueText>£{priceRange[0]}</Stat.ValueText>
        </Stat.Root>
        <Stat.Root alignItems={'flex-end'}>
          <Stat.Label>Max</Stat.Label>
          <Stat.ValueText>£{priceRange[1]}</Stat.ValueText>
        </Stat.Root>
      </HStack>
      <Slider.Root
        min={MIN_PRICE}
        max={MAX_PRICE}
        step={STEP}
        value={priceRange}
        onValueChange={details => setPriceRange(details.value)}
        onValueChangeEnd={details => setPriceRange(details.value)}
        cursor="pointer"
      >
        <Slider.Control>
          <Slider.Track>
            <Slider.Range />
          </Slider.Track>
          <Slider.Thumbs />
        </Slider.Control>
      </Slider.Root>

      <ButtonGroup mt={3}>
        <Button onClick={applyPrice} disabled={!isDirty} rounded={'3xl'}>
          Apply
        </Button>

        {isDirty && (
          <Button variant="ghost" onClick={clearPrice}>
            Clear
          </Button>
        )}
      </ButtonGroup>
    </Box>
  );
};

export default SearchFiltersPrice;
